<template>
  <v-card class="optimize-dialog">
    <v-card-title>
      <span class="toolbar_title">Optimize Plan</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon color="black">mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-row class="ma-0 pa-0 optimize-dialog__content">
      <v-col cols="5" class="px-6 pt-6">
        <v-card outlined class="optimize-type-card mb-4">
          <v-card-title class="pb-2">
            <div>
              <div class="scheduler_title">Optimization Type</div>
            </div>
          </v-card-title>
          <v-card-text>
            <v-radio-group
              :value="optimization.type"
              class="ma-0"
              hide-details
              row
              @change="updateOptimizationField('type', $event)"
            >
              <v-radio
                v-for="option in optimization_type_options"
                :key="option.value"
                :label="option.text"
                :value="option.value"
                color="black"
              ></v-radio>
            </v-radio-group>
          </v-card-text>
        </v-card>
        <v-card outlined class="optimize-card">
          <v-card-title class="pb-2">
            <div>
              <div class="scheduler_title">Target</div>
              <div class="scheduler_subtitle">
                Select the metric and total value for the plan.
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <v-select
              :value="optimization.metric"
              :items="optimization_metric_options"
              item-text="text"
              item-value="value"
              label="Optimize To"
              outlined
              hide-details="auto"
              @change="updateOptimizationField('metric', $event)"
            ></v-select>
            <v-text-field
              :value="optimization.target"
              class="mt-6"
              label="Target Value"
              type="number"
              min="0"
              outlined
              hide-details="auto"
              :prefix="optimization.metric === 'spend' ? '$' : undefined"
              @input="updateOptimizationField('target', $event)"
            ></v-text-field>
            <v-alert
              v-if="optimization_error"
              dense
              outlined
              type="warning"
              class="mt-6 mb-0"
            >
              {{ optimization_error }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="7" class="px-6 pt-6">
        <v-card outlined class="optimize-offset-card">
          <v-card-title class="pb-2">
            <div>
              <div class="scheduler_title">Offsets</div>
              <div class="scheduler_subtitle">
                Guide the optimizer toward or away from plan segments.
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn small outlined color="black" @click="$emit('add_offset')">
              Add Offset
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row
              v-for="(offset, index) in optimization.offsets"
              :key="offset.id"
              dense
              align="center"
              class="optimization-offset-row"
            >
              <v-col cols="3">
                <v-select
                  v-model="offset.level"
                  :items="optimization_offset_levels"
                  item-text="text"
                  item-value="value"
                  label="Level"
                  dense
                  outlined
                  hide-details
                  @change="offset.value = null"
                ></v-select>
              </v-col>
              <v-col cols="5">
                <v-select
                  v-model="offset.value"
                  :items="get_optimization_offset_values(offset.level)"
                  label="Value"
                  dense
                  outlined
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="offset.offset"
                  label="Offset"
                  type="number"
                  suffix="%"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="1" class="text-right">
                <v-btn icon small @click="$emit('remove_offset', index)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-alert
              v-if="optimization.offsets.length === 0"
              dense
              text
              color="blue-grey"
              class="mb-0"
            >
              No offsets selected.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="ma-0 optimize-dialog__button-row" justify="space-around">
      <v-col cols="auto">
        <v-btn
          outlined
          color="black"
          :disabled="!can_run_optimization"
          @click="$emit('run_optimization')"
        >
          <span>Optimize</span>
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: "SweetSpotOptimizeCard",
  props: {
    optimization: {
      type: Object,
      required: true,
    },
    optimization_error: {
      type: String,
      default: "",
    },
    optimization_metric_options: {
      type: Array,
      default: () => [],
    },
    optimization_type_options: {
      type: Array,
      default: () => [],
    },
    optimization_offset_levels: {
      type: Array,
      default: () => [],
    },
    can_run_optimization: {
      type: Boolean,
      default: false,
    },
    get_optimization_offset_values: {
      type: Function,
      required: true,
    },
  },
  methods: {
    updateOptimizationField(field, value) {
      this.$emit("update_optimization_field", { field, value });
    },
  },
};
</script>

<style scoped>
.optimize-dialog {
  display: flex;
  flex-direction: column;
  height: 90vh;
}

.optimize-dialog__content {
  flex: 1 1 auto;
  overflow: hidden;
}

.optimize-dialog__button-row {
  flex: 0 0 auto;
  padding-bottom: 28px;
  padding-top: 18px;
}

.optimize-card {
  height: calc(85% - 16px);
  overflow-y: auto;
}

.optimize-offset-card {
  height: 100%;
  overflow-y: auto;
}

.optimize-type-card {
  height: 15%;
  overflow: visible;
}

.optimization-offset-row + .optimization-offset-row {
  margin-top: 12px;
}
</style>
